import { useRef, useState, useCallback } from "react";
import { Reveal, Label } from "./Reveal";
import { GALLERY } from "../data";

function Comparison({ item }) {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(100, Math.max(0, p)));
  }, []);

  const onDown = () => (dragging.current = true);
  const onUp = () => (dragging.current = false);
  const onMove = (e) => {
    if (!dragging.current) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    setFromClientX(clientX);
  };

  return (
    <div
      ref={containerRef}
      data-testid={`comparison-${item.id}`}
      className="relative aspect-[4/3] w-full overflow-hidden border border-border select-none cursor-ew-resize"
      onMouseMove={onMove}
      onMouseDown={(e) => {
        onDown();
        setFromClientX(e.clientX);
      }}
      onMouseUp={onUp}
      onMouseLeave={onUp}
      onTouchStart={onDown}
      onTouchMove={onMove}
      onTouchEnd={onUp}
    >
      {/* After (base) */}
      <img
        src={item.after}
        alt={`${item.label} after`}
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
      />
      <span className="absolute bottom-4 right-4 z-20 text-[10px] uppercase tracking-[0.2em] bg-acid text-obsidian px-3 py-1.5 font-bold">
        After
      </span>

      {/* Before (clipped) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ width: `${pos}%` }}
      >
        <img
          src={item.before}
          alt={`${item.label} before`}
          className="absolute inset-0 h-full object-cover img-treat"
          style={{ width: containerRef.current?.offsetWidth || "100%" }}
          draggable={false}
        />
        <span className="absolute bottom-4 left-4 z-20 text-[10px] uppercase tracking-[0.2em] bg-obsidian/80 text-white px-3 py-1.5 font-bold border border-white/20">
          Before
        </span>
      </div>

      {/* Handle */}
      <div
        className="absolute top-0 bottom-0 z-10 w-0.5 bg-acid"
        style={{ left: `${pos}%` }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 bg-acid flex items-center justify-center">
          <span className="text-obsidian text-lg font-bold">⇄</span>
        </div>
      </div>
    </div>
  );
}

export default function Gallery() {
  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="bg-surface py-24 lg:py-32 border-b border-border"
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <Label>The Proof</Label>
            <h2 className="font-display uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[0.95] text-white">
              Before / After.
            </h2>
          </div>
          <p className="max-w-sm text-zinc-400 font-light">
            Drag the divider. Real commercial results across warehouse floors,
            concrete and building exteriors.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {GALLERY.map((item, i) => (
            <Reveal key={item.id} delay={i * 0.1}>
              <Comparison item={item} />
              <div className="mt-4 flex items-center gap-3">
                <span className="w-2 h-2 bg-acid" />
                <span className="text-xs uppercase tracking-[0.2em] text-zinc-400">
                  {item.label}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
